import { css, html, repeat, when } from '@microsoft/fast-element';
import { FASTElementLayout } from '@microsoft/fast-router';
import CleanTradeLogo from '../assets/CleanTrade-logo-transparent.png';
import { SHARED_DASHBOARD_GRID_TABS } from '../components/common/negotiations-grid/negotiations-grid';
import { mixinRobotoRegular } from '../styles';
import { hostEnv } from '../main';
import { ButtonAppearance } from '../utils/notify';
import CleanSiteLogo from '../assets/CleanSite-logo-transparent.png';

const baseLayoutCss = css`
  .container {
    width: 100%;
    height: 100%;
    display: block;
    position: relative;
  }

  .content {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 48px;
  }

  .content.login {
    right: 0;
  }

  .menu-button-selected {
    position: relative;
    height: 60px;
    background-color: transparent;
    font-size: 14px;
    font-family: 'Roboto-Medium', 'Roboto Medium', 'Roboto', sans-serif;
    font-weight: 500;
    color: var(--primary-color);
    margin-top: 0;
    margin-bottom: 0;
  }

  .menu-button-selected::before {
    content: '';
    position: absolute;
    height: 3px;
    background: linear-gradient(90deg, var(--secondary-color) 35%, var(--primary-color) 100%);
    bottom: 0;
    left: 0;
    right: 0;
  }

  .menu-button-selected:hover {
    background: var(--neutral-fill-rest) !important;
    border-radius: 0;
  }

  .menu-button {
    height: 60px;
    border-radius: 0;
    background-color: transparent;
    color: var(--header-text-color-1);
    margin-top: 0;
    margin-bottom: 0;
    font-size: 14px;
    font-family: 'Roboto-Medium', 'Roboto Medium', 'Roboto', sans-serif;
  }

  .menu-button:hover {
    background-color: var(--button-hover-bg);
    color: white;
  }
`;

export function loginLayout() {
  const disabledCss = css`
    content.login {
      display: disabled;
    }
  `;
  return new FASTElementLayout(
    html`
      <div class="container">
        <div class="content login">
          <slot></slot>
        </div>
      </div>
    `,
    (hostEnv.includes('resurety.app')
     || hostEnv.includes('cascade-uat.genesis.global')
     || hostEnv.includes('10.253.21.79')) ? disabledCss : baseLayoutCss,
  );
}

export const loginLogo = css`
  content: url(${CleanTradeLogo});
  width: 200px;
  height: 29px;
`;

export const loginBackground = css`
  :host {
    background: #0273b5;
    background-size: auto;
  }
`;

export const defaultLayout = new FASTElementLayout(
  html`
    <div class="container">
      <custom-header logo-src="${CleanTradeLogo}">
        ${repeat(
          (x) => x?.config.allCleanTradeRoutes(),
          html`
            <zero-button
              class="${(x, c) =>
                c.parent.config.currentPath === x.path ? 'menu-button-selected' : 'menu-button'}"
              slot="routes"
              :disabled=${(x, c) =>
                x.title === 'Explore' || x.title === 'Post Trade'
                  ? !c.parent.config.enableExplore
                  : false}
              value="${(x) => x.index}"
              @click=${(x, c) => {
                if (x.path) {
                  c.parent.config.navigationService.setCurrentPath(x.path);
                  c.parent.navigation.navigateTo(`/${x.path}`);
                } else {
                  window.open(c.parent.config.getRemapUrl(), '_blank');
                }
              }}
            >
              <zero-icon variant="${(x) => x.variant}" name="${(x) => x.icon}"></zero-icon>
              ${(x) => x.title}
              ${when(
                (x, c) => c.parent.config.totalNegotiationCount > 0 && x.title === 'Execution',
                html`
                  <span class="countNumber">
                    ${(x, c) => c.parent.config.totalNegotiationCount}
                  </span>
                `,
              )}
            </zero-button>
          `,
        )}
        <img slot="routes-end" width="100" height="20px" src=${CleanSiteLogo} />
        ${repeat(
          (x) => x?.config.allCleanSightRoutes(),
          html`
            <zero-button
              class="menu-button"
              slot="routes-end"
              value="${(x) => x.index}"
              @click=${(x, c) => window.open(c.parent.config.getRemapUrl(), '_blank')}
            >
              <zero-icon variant="${(x) => x.variant}" name="${(x) => x.icon}"></zero-icon>
              ${(x) => x.title}
            </zero-button>
          `,
        )}
        <span slot="group-title-1">GROUP SLOT</span>
        <zero-tree-view slot="nav-items-1">
          <zero-tree-item>
            <zero-icon variant="solid" name="location-arrow"></zero-icon>
            Slot Tree Item
          </zero-tree-item>
          <zero-tree-item>
            <zero-icon variant="solid" name="location-arrow"></zero-icon>
            Slot Tree Item
          </zero-tree-item>
        </zero-tree-view>
        <span slot="group-title-2">GROUP SLOT 2</span>
        <zero-tree-view slot="nav-items-2">
          <zero-tree-item>
            <zero-icon variant="solid" name="location-arrow"></zero-icon>
            Slot Tree Item 2
          </zero-tree-item>
          <zero-tree-item>
            <zero-icon variant="solid" name="location-arrow"></zero-icon>
            Slot Tree Item 2
          </zero-tree-item>
        </zero-tree-view>
      </custom-header>
      <sef-agreement-required></sef-agreement-required>
      <side-menu></side-menu>
      <div class="content">
        <slot></slot>
      </div>
      <structured-chat></structured-chat>
      <user-modal></user-modal>
      <knowledge-block-modal></knowledge-block-modal>
      <rfq-detail-modal></rfq-detail-modal>
      <vppa-detail-modal></vppa-detail-modal>
      <negotiation-detail-modal></negotiation-detail-modal>
      <zero-notification-listener
        resource-name="ALL_NOTIFY_ALERT_RECORDS"
        :toastButtons="${(x): any[] => [
          // ToastButton after updating
          {
            condition: ({ details }) => {
              if (
                details.TOPIC === 'NOTIFY_NEGOTIATION_CREATED' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_COUNTEROFFER'
              ) {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.notificationService.openStructuredChat(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View Offer',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (
                details.TOPIC === 'NOTIFY_NEGOTIATION_ACCEPTED' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_CONFIRMED'
              ) {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.notificationService.openStructuredChat(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View Pre-Trade Summary',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (
                details.TOPIC === 'NOTIFY_NEGOTIATION_FULL_CONFIRMED' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_REJECTED'
              ) {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.notificationService.openStructuredChat(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View Conversation',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_NEGOTIATION_PENDING_EXECUTION') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToExecution();
            },
            placeholder: 'View Trade',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_NEGOTIATION_EXPIRED_NOT_CONFIRMED') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToExecution();
            },
            placeholder: 'Return to Contracting Module',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_NEGOTIATION_FULLY_EXECUTED') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToExecution();
            },
            placeholder: 'View Dashboard',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_NEGOTIATION_TRADED_AWAY') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToDashboard(
                SHARED_DASHBOARD_GRID_TABS.TERMINATED_TAB,
              );
            },
            placeholder: `View History`,
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_NEGOTIATION_IN_PROGRESS_ARTERIA') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToExecution();
            },
            placeholder: 'Return to Negotiation',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_PROJECT_LIVE') {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToProjectPage(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View Project',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_RFQ_CREATED') {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.rfqService.openRfqDetailModal(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View RFQ',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_VPPA_ACTIVE') {
                return true;
              }
              return false;
            },
            action: ({ details, toastElement }) => {
              toastElement.close();
              x.config.vppaService.openVppaDetailModal(details.TABLE_ENTITY_ID);
            },
            placeholder: 'View VPPA',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (details.TOPIC === 'NOTIFY_VPPA_ACTIVE') {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToMarketPage();
            },
            placeholder: 'View Market',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (
                details.TOPIC === 'NOTIFY_BUYER_NEGOTIATION_AUTO_EXECUTED' ||
                details.TOPIC === 'NOTIFY_SELLER_NEGOTIATION_AUTO_EXECUTED' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_AUTO_EXECUTED'
              ) {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
              x.config.notificationService.redirectToExecution();
            },
            placeholder: 'View Auto-Executed Contracts',
            appearance: ButtonAppearance.PRIMARY,
          },
          {
            condition: ({ details }) => {
              if (
                details.TOPIC === 'NOTIFY_NEGOTIATION_EXPIRED_NOT_CONFIRMED' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_IN_PROGRESS_ARTERIA' ||
                details.TOPIC === 'NOTIFY_NEGOTIATION_TRADED_AWAY'
              ) {
                return true;
              }
              return false;
            },
            action: ({ toastElement }) => {
              toastElement.close();
            },
            placeholder: 'Dismiss',
            appearance: ButtonAppearance.SECONDARY,
          },
        ]}"
      ></zero-notification-listener>
    </div>
  `,
  css`
    ${baseLayoutCss}

    .content {
      top: 60px;
      background-color: var(--neutral-layer-1);
      contain: content;
    }

    foundation-header {
      z-index: 999;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      align-items: center;
      border: none;
    }

    structured-chat {
      display: contents;
    }

    user-modal {
      display: contents;
    }

    knowledge-block-modal {
      display: contents;
    }

    .countNumber {
      background-color: rgba(249, 100, 77, 0.3);
      border: 1px solid #f9644d;
      border-radius: 25px;
      width: 23px;
      height: 23px;
      line-height: 23px;
      margin-left: 10px;
      color: #fff;
      font-size: 13px;
      ${mixinRobotoRegular}
    }

    zero-button::part(content) {
      display: flex;
      align-items: center;
    }
  `,
);

export const noMenusLayout = new FASTElementLayout(
  html`
    <div class="content">
      <slot></slot>
    </div>
  `,
  css`
    ${baseLayoutCss}

    .content {
      inset: 0;
    }
  `,
);
